/**
 * Guide Pricing Management API Service
 *
 * SCRUM-1318 / SCRUM-1282 / SCRUM-1283 / SCRUM-1285 / SCRUM-1287 / SCRUM-1288
 * Guide-facing API calls for season, modifier, promotion, preview, and analytics.
 */

import apiRequest from './api';

const BASE = '/api/v1/guides/experiences';

// ============================================================================
// Seasons
// ============================================================================

export async function getSeasons(experienceId) {
  return apiRequest(`${BASE}/${experienceId}/pricing/seasons`);
}

export async function addSeason(experienceId, data) {
  return apiRequest(`${BASE}/${experienceId}/pricing/seasons`, {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

export async function updateSeason(experienceId, seasonId, data) {
  return apiRequest(`${BASE}/${experienceId}/pricing/seasons/${seasonId}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
}

export async function deleteSeason(experienceId, seasonId) {
  return apiRequest(`${BASE}/${experienceId}/pricing/seasons/${seasonId}`, {
    method: 'DELETE'
  });
}

// ============================================================================
// Season Types (read-only catalog)
// ============================================================================

export async function getSeasonTypes(experienceId) {
  return apiRequest(`${BASE}/${experienceId}/pricing/templates/seasons`);
}

// ============================================================================
// Modifiers
// ============================================================================

export async function getModifiers(experienceId) {
  return apiRequest(`${BASE}/${experienceId}/pricing/modifiers`);
}

export async function addModifier(experienceId, data) {
  return apiRequest(`${BASE}/${experienceId}/pricing/modifiers`, {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

export async function updateModifier(experienceId, configId, data) {
  return apiRequest(`${BASE}/${experienceId}/pricing/modifiers/${configId}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
}

export async function toggleModifier(experienceId, configId, isEnabled) {
  return apiRequest(`${BASE}/${experienceId}/pricing/modifiers/${configId}/toggle`, {
    method: 'PUT',
    body: JSON.stringify({ isEnabled })
  });
}

export async function deleteModifier(experienceId, configId) {
  return apiRequest(`${BASE}/${experienceId}/pricing/modifiers/${configId}`, {
    method: 'DELETE'
  });
}

// ============================================================================
// Promotions
// ============================================================================

export async function getPromotions(experienceId) {
  return apiRequest(`${BASE}/${experienceId}/pricing/promotions`);
}

export async function createPromotion(experienceId, data) {
  return apiRequest(`${BASE}/${experienceId}/pricing/promotions`, {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

export async function updatePromotion(experienceId, promotionId, data) {
  return apiRequest(`${BASE}/${experienceId}/pricing/promotions/${promotionId}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
}

export async function deletePromotion(experienceId, promotionId) {
  return apiRequest(`${BASE}/${experienceId}/pricing/promotions/${promotionId}`, {
    method: 'DELETE'
  });
}

// ============================================================================
// Preview
// ============================================================================

export async function previewPricing(experienceId, data) {
  return apiRequest(`${BASE}/${experienceId}/pricing/preview`, {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

// ============================================================================
// Analytics
// ============================================================================

export async function getPricingAnalytics(experienceId) {
  return apiRequest(`${BASE}/${experienceId}/pricing/analytics`);
}

// ============================================================================
// Participant Types (SCRUM-1292)
// ============================================================================

export async function getParticipantTypes(experienceId) {
  return apiRequest(`${BASE}/${experienceId}/pricing/participant-types`);
}

export async function createParticipantType(experienceId, data) {
  return apiRequest(`${BASE}/${experienceId}/pricing/participant-types`, {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

export async function updateParticipantType(experienceId, typeId, data) {
  return apiRequest(`${BASE}/${experienceId}/pricing/participant-types/${typeId}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
}

export async function deactivateParticipantType(experienceId, typeId) {
  return apiRequest(`${BASE}/${experienceId}/pricing/participant-types/${typeId}`, {
    method: 'DELETE'
  });
}

// ============================================================================
// Pricing Config
// ============================================================================

export async function getPricingConfig(experienceId) {
  return apiRequest(`${BASE}/${experienceId}/pricing/config`);
}

export default {
  getSeasons,
  addSeason,
  updateSeason,
  deleteSeason,
  getSeasonTypes,
  getModifiers,
  addModifier,
  updateModifier,
  toggleModifier,
  deleteModifier,
  getPromotions,
  createPromotion,
  updatePromotion,
  deletePromotion,
  previewPricing,
  getPricingAnalytics,
  getPricingConfig,
  getParticipantTypes,
  createParticipantType,
  updateParticipantType,
  deactivateParticipantType
};
